/**
 * Exit-animation presence shared by every overlay primitive.
 *
 * Radix keeps a layer mounted until its exit animation ends, then unmounts it.
 * A primitive that tears the layer down the instant `data-state` flips to
 * `closed` cuts the animation off; this helper is the other half: flip
 * `data-state="closed"` first, then hand the unmount to {@link exitWhenDone}.
 *
 * ```ts
 * setState('closed', content);
 * const cancel = exitWhenDone(content, () => layer.destroy());
 * ```
 *
 * `done` runs exactly once, and:
 *
 * - **synchronously** when the element has nothing to wait for: no running CSS
 *   animation or transition, or the user prefers reduced motion;
 * - when every finite animation on the element (CSS animations and transitions,
 *   read through `getAnimations()`) has finished or been cancelled;
 * - on engines without `getAnimations()` (happy-dom, old WebViews), when the
 *   element's own `animationend` / `transitionend` fires, or after the longest
 *   computed `animation-*` / `transition-*` duration plus delay, whichever is
 *   first, so a missing event can never leave a closed layer mounted.
 *
 * It returns a `cancel()` that stops waiting without calling `done` (use it
 * when the layer reopens mid-exit, or on destroy).
 */
const GRACE_MS = 50;
/** True when the user asked the OS for reduced motion. */
export function prefersReducedMotion() {
    return (typeof globalThis.matchMedia === 'function' &&
        globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches);
}
function parseTimes(value) {
    return value
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part.length > 0)
        .map((part) => {
        const seconds = part.endsWith('ms')
            ? Number.parseFloat(part) / 1000
            : Number.parseFloat(part);
        return Number.isFinite(seconds) ? seconds * 1000 : 0;
    });
}
function longest(durations, delays, counts) {
    let max = 0;
    for (let i = 0; i < durations.length; i += 1) {
        const duration = durations[i] ?? 0;
        if (duration <= 0)
            continue;
        const delay = delays.length > 0 ? (delays[i % delays.length] ?? 0) : 0;
        const iterations = counts && counts.length > 0 ? (counts[i % counts.length] ?? 1) : 1;
        if (!Number.isFinite(iterations))
            continue;
        max = Math.max(max, delay + duration * iterations);
    }
    return max;
}
/** Longest animation or transition on `element` according to computed style, in ms. */
export function computedExitMs(element) {
    const style = getComputedStyle(element);
    const names = style.animationName.split(',').map((name) => name.trim());
    const animationDurations = parseTimes(style.animationDuration).map((duration, i) => names[i % names.length] === 'none' ? 0 : duration);
    const counts = style.animationIterationCount
        .split(',')
        .map((count) => count.trim() === 'infinite' ? Number.POSITIVE_INFINITY : Number.parseFloat(count));
    const animation = longest(animationDurations, parseTimes(style.animationDelay), counts);
    const transition = longest(parseTimes(style.transitionDuration), parseTimes(style.transitionDelay), null);
    return Math.max(animation, transition);
}
function runningAnimations(element) {
    if (typeof element.getAnimations !== 'function')
        return null;
    return element.getAnimations().filter((animation) => {
        const iterations = animation.effect?.getComputedTiming().iterations ?? 1;
        return Number.isFinite(iterations) && animation.playState !== 'finished';
    });
}
/**
 * Calls `done` once `element`'s exit animation has finished (see file header).
 * Returns `cancel()`, which prevents `done` from running.
 */
export function exitWhenDone(element, done) {
    let settled = false;
    let cleanup = () => { };
    const finish = () => {
        if (settled)
            return;
        settled = true;
        cleanup();
        done();
    };
    const cancel = () => {
        settled = true;
        cleanup();
    };
    if (prefersReducedMotion() || !element.isConnected) {
        finish();
        return cancel;
    }
    const animations = runningAnimations(element);
    if (animations) {
        if (animations.length === 0) {
            finish();
            return cancel;
        }
        // `finished` rejects when an animation is cancelled; either outcome ends the wait.
        void Promise.allSettled(animations.map((animation) => animation.finished)).then(finish);
        return cancel;
    }
    const wait = computedExitMs(element);
    if (wait <= 0) {
        finish();
        return cancel;
    }
    const onEnd = (event) => {
        if (event.target === element)
            finish();
    };
    element.addEventListener('animationend', onEnd);
    element.addEventListener('transitionend', onEnd);
    element.addEventListener('animationcancel', onEnd);
    element.addEventListener('transitioncancel', onEnd);
    const timer = setTimeout(finish, wait + GRACE_MS);
    cleanup = () => {
        clearTimeout(timer);
        element.removeEventListener('animationend', onEnd);
        element.removeEventListener('transitionend', onEnd);
        element.removeEventListener('animationcancel', onEnd);
        element.removeEventListener('transitioncancel', onEnd);
    };
    return cancel;
}
